import { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { EventDebugContext, type EventLogInput } from "@/contexts/eventDebugContext";

/**
 * A map of event names to their listener function signatures.
 * Used as a generic constraint for typed event systems.
 * 
 * @example
 * type MyEvents = {
 *   userLoggedIn: (userId: string) => void;
 *   itemAdded: (item: Item, quantity: number) => void;
 * }
 */
export type EventListenerMap = Record<string, EventListenerFn>;

/**
 * A generic event listener function that can accept arguments.
 * 
 * Note: Uses `any[]` for the base type due to TypeScript's contravariance rules
 * for function parameters. The actual type safety comes from the generic `T`
 * parameter at call sites - users always interact with fully typed signatures.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type EventListenerFn = (...args: any[]) => void;

/**
 * Options for subscribing to an event.
 */
export interface SubscribeOptions {
  /** 
   * A descriptive name for this handler, used for debugging.
   * If not provided, will use the function's name property or generate one.
   * 
   * @example
   * subscribe("onClick", handler, { name: "Button.handleClick" });
   */
  name?: string;
}

/**
 * Internal storage for a listener with its metadata.
 */
interface ListenerEntry {
  /** The actual listener function */
  fn: EventListenerFn;
  /** Name for debugging purposes */
  name: string;
}

/**
 * A typed emitter object where each key is an event name and each value
 * is a function to emit that event with the correct parameters.
 * 
 * @template T - The event listener map defining available events
 * @template E - The subset of event keys (defaults to all keys)
 * 
 * @example
 * // Given events: { onClick: (x: number, y: number) => void }
 * // Emitter provides: { onClick: (x: number, y: number) => void }
 * emit.onClick(100, 200);
 */
export type Emitter<T extends EventListenerMap, E extends keyof T = keyof T> = {
  [K in keyof T]: (...args: Parameters<T[K]>) => void;
}

/**
 * A function type for subscribing to events.
 * 
 * @template T - The event listener map defining available events
 * 
 * @example
 * // Basic subscription
 * subscribe("userLoggedIn", (userId) => {
 *   console.log(`User ${userId} logged in`);
 * });
 * 
 * // With debug name
 * subscribe("userLoggedIn", handler, { name: "AuthPanel.onLogin" });
 */
export type Subscriber<T extends EventListenerMap> = <E extends keyof T = keyof T>(
  event: E, 
  listener: T[E],
  options?: SubscribeOptions
) => void;

/**
 * A function type for unsubscribing from events.
 * 
 * @template T - The event listener map defining available events
 * 
 * @example
 * const handler = (userId: string) => console.log(userId);
 * subscribe("userLoggedIn", handler);
 * // Later...
 * unsubscribe("userLoggedIn", handler);
 */
export type UnSubscriber<T extends EventListenerMap> = <E extends keyof T = keyof T>(event: E, listener: T[E]) => void;

/**
 * The complete event listener interface returned by useEventListeners.
 * Provides type-safe subscribe, unsubscribe, and emit capabilities.
 * 
 * @template T - The event listener map defining available events
 * 
 * @property subscribe - Function to add a listener for an event
 * @property unsubscribe - Function to remove a listener from an event
 * @property emit - Object with methods to emit each event type
 * 
 * @example
 * type MyEvents = {
 *   dataLoaded: (data: Data[]) => void;
 *   error: (message: string) => void;
 * };
 * 
 * const events: EventListeners<MyEvents> = useEventListeners<MyEvents>();
 * 
 * // Subscribe
 * events.subscribe("dataLoaded", (data) => console.log(data));
 * 
 * // Emit
 * events.emit.dataLoaded([{ id: 1 }]);
 */
export type EventListeners<T extends EventListenerMap> = {
  readonly subscribe: Subscriber<T>;
  readonly unsubscribe: UnSubscriber<T>;
  readonly emit: Emitter<T>;
  /** The namespace of this event bus (for debugging) */
  readonly namespace: string;
}

/**
 * A React hook that provides type-safe event handling functionality for components.
 * 
 * Returns a stable reference (memoized) containing subscribe, unsubscribe, and emit
 * capabilities. All returned functions maintain referential stability across renders,
 * making them safe to use in dependency arrays.
 * 
 * Events are stored in a ref to avoid re-renders when listeners are added/removed.
 * All listeners are automatically cleaned up when the component unmounts.
 * 
 * @template T - An EventListenerMap defining the available events and their signatures
 * 
 * @returns An object with subscribe, unsubscribe, and emit properties
 * 
 * @example
 * // Define your event types
 * type ChatEvents = {
 *   messageReceived: (message: Message) => void;
 *   userJoined: (userId: string, username: string) => void;
 *   roomClosed: () => void;
 * };
 * 
 * // In your component or context
 * const events = useEventListeners<ChatEvents>();
 * 
 * // Subscribe to events (typically in useEffect)
 * useEffect(() => {
 *   const handleMessage = (message: Message) => {
 *     console.log("New message:", message);
 *   };
 *   
 *   events.subscribe("messageReceived", handleMessage);
 *   
 *   return () => {
 *     events.unsubscribe("messageReceived", handleMessage);
 *   };
 * }, [events]);
 * 
 * // Emit events from anywhere
 * events.emit.messageReceived({ id: "1", text: "Hello!" });
 * events.emit.userJoined("user-123", "Alice");
 * events.emit.roomClosed();
 * 
 * @example
 * // Passing events through context
 * const MyContext = createContext<EventListeners<MyEvents> | null>(null);
 * 
 * function MyProvider({ children }) {
 *   const events = useEventListeners<MyEvents>();
 *   return (
 *     <MyContext.Provider value={events}>
 *       {children}
 *     </MyContext.Provider>
 *   );
 * }
 */
export const useEventListeners = <T extends EventListenerMap = Record<string, EventListenerFn>>(
  namespace?: string
): EventListeners<T> => {
  // Get debug context (null if not inside EventDebugProvider)
  // Use ref to avoid recreating emit when debug context changes
  const debugContext = useContext(EventDebugContext);
  const debugContextRef = useRef(debugContext);
  debugContextRef.current = debugContext;
  
  // Generate stable namespace
  const namespaceRef = useRef(namespace ?? `bus-${Math.random().toString(36).slice(2, 8)}`);
  
  // Store listeners with their metadata
  const listenersRef = useRef<Map<keyof T, Set<ListenerEntry>>>(null);
  
  // Counter for generating handler names
  const handlerCounterRef = useRef(0);

  /**
   * Generate a handler name when none is provided.
   */
  const generateHandlerName = (fn: EventListenerFn, index: number): string => {
    if (fn.name && fn.name !== "") {
      return fn.name;
    }
    return `handler-${index}`;
  };

  /**
   * Subscribe a listener to an event.
   * The same listener can be added multiple times - it will only be called once per emit.
   */
  const subscribe = useCallback<Subscriber<T>>((event, listener, options) => {
    if (!listenersRef.current) {
      listenersRef.current = new Map();
    }

    if (!listenersRef.current.has(event)) {
      listenersRef.current.set(event, new Set());
    }

    // Check if this listener is already registered (by function reference)
    const existingEntries = listenersRef.current.get(event);
    if (existingEntries) {
      for (const entry of existingEntries) {
        if (entry.fn === listener) {
          return; // Already registered
        }
      }
    }

    // Create entry with name
    const entry: ListenerEntry = {
      fn: listener as EventListenerFn,
      name: options?.name ?? generateHandlerName(listener as EventListenerFn, handlerCounterRef.current++),
    };

    listenersRef.current.get(event)?.add(entry);
  }, [])

  /**
   * Unsubscribe a listener from an event.
   * Must pass the exact same function reference that was used to subscribe.
   */
  const unsubscribe = useCallback<UnSubscriber<T>>((event, listener) => {
    if (!listenersRef.current) {
      return;
    }

    const entries = listenersRef.current.get(event);
    if (!entries) {
      return;
    }

    // Find and remove the entry with matching function reference
    for (const entry of entries) {
      if (entry.fn === listener) {
        entries.delete(entry);
        break;
      }
    }
  }, [])

  /**
   * Emitter object using Proxy to provide typed emit methods for each event.
   * Access events as properties: emit.eventName(arg1, arg2)
   */
  const emit: Emitter<T> = useMemo(() => {
    return new Proxy({} as Emitter<T>, {
      get(_, event: string) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (...args: any[]) => {
          const entries = listenersRef.current?.get(event as keyof T);
          
          // Collect handler info for debugging
          const handlerNames: string[] = [];
          const handlerCount = entries?.size ?? 0;
          
          if (entries) {
            for (const entry of entries) {
              handlerNames.push(entry.name);
            }
          }

          // Log to debug context if enabled (use ref to avoid dependency on context changes)
          const debug = debugContextRef.current;
          if (debug?.enabled && debug.shouldLog(namespaceRef.current, event)) {
            const logInput: EventLogInput = {
              namespace: namespaceRef.current,
              event,
              args,
              handlerCount,
              handlerNames,
            };
            debug.logEvent(logInput);
          }

          // Execute handlers
          if (!entries || entries.size === 0) {
            return;
          }

          for (const entry of entries) {
            try {
              entry.fn(...args);
            } catch (error) {
              console.error(
                `[useEventListeners] Error in handler "${entry.name}" for event "${event}" (namespace: ${namespaceRef.current}):`, 
                error
              );
            }
          }
        };
      }
    });
  }, []); // No dependencies - uses refs for all external values

  // Initialize the listeners map and clean up all listeners on unmount
  useEffect(() => {
    if (!listenersRef.current) {
      listenersRef.current = new Map();
    }

    return () => {
      listenersRef.current?.forEach((entries, event) => {
        entries.forEach((entry) => {
          unsubscribe(event, entry.fn as T[typeof event]);
        });
      });
    }
  }, [unsubscribe]);

  return useMemo(() => ({
    subscribe,
    unsubscribe,
    emit,
    namespace: namespaceRef.current,
  } as const), [subscribe, unsubscribe, emit]);
}

/**
 * A function type for subscribing to events scoped by an ID.
 * Useful for managing events across multiple instances (e.g., multiple chat rooms).
 * 
 * @template T - The event listener map defining available events
 * @template E - The event key type
 * 
 * @example
 * subscribe("room-123", "messageReceived", (message) => {
 *   console.log(`Room 123 received: ${message}`);
 * });
 */
export type IdSubscriber<T extends EventListenerMap, E extends keyof T> = (
  id: string, 
  event: E, 
  listener: T[E],
  options?: SubscribeOptions
) => void;

/**
 * A function type for unsubscribing from ID-scoped events.
 * 
 * @template T - The event listener map defining available events
 * @template E - The event key type
 */
export type IdUnsubscriber<T extends EventListenerMap, E extends keyof T> = (id: string, event: E, listener: T[E]) => void;

/**
 * A nested emitter object where the first level is the ID and the second level
 * contains the event emitters for that ID.
 * 
 * @template T - The event listener map defining available events
 * @template E - The event key type
 * 
 * @example
 * // Emit to specific ID
 * emit["room-123"].messageReceived({ text: "Hello!" });
 * emit["user-456"].statusChanged("online");
 */
export type IdEmitter<T extends EventListenerMap, E extends keyof T = keyof T> = {
  [id: string]: Emitter<T, E>;
}

/**
 * The complete ID-scoped event listener interface returned by useIdEventListener.
 * 
 * @template T - The event listener map defining available events
 * 
 * @property subscribe - Function to add a listener for an event on a specific ID
 * @property unsubscribe - Function to remove a listener from an event on a specific ID
 * @property emit - Nested object to emit events: emit[id].eventName(args)
 */
export type IdEventListener<T extends EventListenerMap> = {
  subscribe: IdSubscriber<T, keyof T>;
  unsubscribe: IdUnsubscriber<T, keyof T>;
  emit: IdEmitter<T, keyof T>;
  /** The namespace of this event bus (for debugging) */
  namespace: string;
}

/**
 * A React hook that provides ID-scoped event handling functionality.
 * 
 * This is useful when you need to manage events for multiple instances of something,
 * such as multiple chat rooms, multiple file uploads, or multiple user sessions.
 * Events are namespaced by ID, so listeners for "room-1" won't receive events
 * emitted to "room-2".
 * 
 * Internally combines the ID and event name into a composite key (e.g., "room-1:messageReceived").
 * Returns stable references (memoized) for all functions.
 * 
 * @template T - An EventListenerMap defining the available events and their signatures
 * 
 * @returns An object with subscribe, unsubscribe, and emit properties (ID-scoped)
 * 
 * @example
 * // Define events for a chat room
 * type RoomEvents = {
 *   messageReceived: (message: Message) => void;
 *   userJoined: (userId: string) => void;
 *   userLeft: (userId: string) => void;
 * };
 * 
 * // In your component or context
 * const roomEvents = useIdEventListener<RoomEvents>();
 * 
 * // Subscribe to events for a specific room
 * useEffect(() => {
 *   const roomId = "room-123";
 *   
 *   const handleMessage = (message: Message) => {
 *     console.log(`[${roomId}] New message:`, message);
 *   };
 *   
 *   roomEvents.subscribe(roomId, "messageReceived", handleMessage);
 *   
 *   return () => {
 *     roomEvents.unsubscribe(roomId, "messageReceived", handleMessage);
 *   };
 * }, [roomEvents]);
 * 
 * // Emit events to a specific room
 * roomEvents.emit["room-123"].messageReceived({ id: "1", text: "Hello!" });
 * roomEvents.emit["room-456"].userJoined("user-789");
 * 
 * @example
 * // Managing multiple file uploads
 * type UploadEvents = {
 *   progress: (percent: number) => void;
 *   complete: (url: string) => void;
 *   error: (message: string) => void;
 * };
 * 
 * const uploadEvents = useIdEventListener<UploadEvents>();
 * 
 * // Each upload gets its own event stream
 * uploadEvents.emit["upload-1"].progress(50);
 * uploadEvents.emit["upload-2"].complete("https://...");
 */
export const useIdEventListener = <T extends EventListenerMap>(
  namespace?: string
): IdEventListener<T> => {
  // Use the base hook with composite keys (id:event)
  const { subscribe, unsubscribe, emit, namespace: resolvedNamespace } = 
    useEventListeners<Record<string, EventListenerFn>>(namespace);

  /**
   * Subscribe to an event for a specific ID.
   */
  const idSubscribe = useCallback<IdSubscriber<T, keyof T>>((id, event, listener, options) => {
    subscribe(`${id}:${String(event)}`, listener as EventListenerFn, options);
  }, [subscribe])

  /**
   * Unsubscribe from an event for a specific ID.
   */
  const idUnsubscribe = useCallback<IdUnsubscriber<T, keyof T>>((id, event, listener) => {
    unsubscribe(`${id}:${String(event)}`, listener as EventListenerFn);
  }, [unsubscribe])

  /**
   * Nested Proxy emitter: emit[id].eventName(args)
   * First proxy resolves the ID, second proxy resolves the event name.
   */
  const idEmitter: IdEmitter<T, keyof T> = useMemo(() => {
    return new Proxy({} as IdEmitter<T, keyof T>, {
      get(_, id: string) {
        return new Proxy({} as Emitter<T, keyof T>, {
          get(_, event: string) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return (...args: any[]) => {
              emit[`${id}:${event}`](...args);
            }
          }
        });
      }
    });
  }, [emit]);

  return useMemo(() => ({
    subscribe: idSubscribe,
    unsubscribe: idUnsubscribe,
    emit: idEmitter,
    namespace: resolvedNamespace,
  }), [idSubscribe, idUnsubscribe, idEmitter, resolvedNamespace]);
}